import { useState, useEffect, useRef } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import type { Phase } from '@/types/api';
import { detectPhasesFromVideo } from '@/services/phaseApi';
import { CONFIDENCE, PHASE_CONFIG } from '@/utils/constants';

interface PhaseDetectionPreviewProps {
  videoId: string;
  fps: number;
  totalFrames: number;
  onPhasesAccepted: (phases: Phase[]) => void;
  onSkip: () => void;
  onEditPhases?: (phases: Phase[]) => void;
  /**
   * Trim boundaries set by the coach in VideoTrimTool (B-2026-0102 Phase 1).
   * When provided, phase detection runs only on the trimmed clip and a banner
   * is shown confirming the effective time range.
   */
  trimStartFrame?: number;
  trimEndFrame?: number;
}

interface PhaseWithConfidence extends Phase {
  confidence?: number;
  label?: string; // Some phase sources use label instead of name
}

export function PhaseDetectionPreview({
  videoId,
  fps,
  totalFrames,
  onPhasesAccepted,
  onSkip,
  onEditPhases,
  trimStartFrame,
  trimEndFrame,
}: PhaseDetectionPreviewProps) {
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedPhases, setDetectedPhases] = useState<PhaseWithConfidence[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasLowConfidence, setHasLowConfidence] = useState(false);

  // Track the echoed trim range from the most recent detection response
  const [appliedTrimStart, setAppliedTrimStart] = useState<number | undefined>(undefined);
  const [appliedTrimEnd, setAppliedTrimEnd] = useState<number | undefined>(undefined);

  const runDetection = async (cancelled?: { current: boolean }) => {
    setIsDetecting(true);
    setError(null);
    setAppliedTrimStart(undefined);
    setAppliedTrimEnd(undefined);

    try {
      const result = await detectPhasesFromVideo(videoId, {
        method: 'velocity',
        min_phase_duration: PHASE_CONFIG.MIN_PHASE_DURATION_FRAMES,
        // B-2026-0102 Phase 1: pass coach-defined trim boundaries
        ...(trimStartFrame !== undefined && trimStartFrame > 0
          ? { trim_start_frame: trimStartFrame }
          : {}),
        ...(trimEndFrame !== undefined && trimEndFrame < totalFrames - 1
          ? { trim_end_frame: trimEndFrame }
          : {}),
      });

      if (cancelled?.current) return;

      const phases = result.detected_phases || [];
      const scores = result.confidence_scores || [];

      // Attach confidence scores to phases
      const phasesWithConfidence = phases.map((phase: Phase, index: number) => ({
        ...phase,
        confidence: scores[index] || 0.5,
      }));

      setDetectedPhases(phasesWithConfidence);

      // Check if any phase has low confidence
      const lowConf = scores.some((score: number) => score < CONFIDENCE.MEDIUM);
      setHasLowConfidence(lowConf);

      // Capture echoed trim range from the response (B-2026-0102 Phase 1).
      // Backend echoes back the applied trim_start_frame / trim_end_frame so we
      // can display a confirmed "Phases detected in your clip" banner.
      if (result.trim_start_frame !== undefined) {
        setAppliedTrimStart(result.trim_start_frame);
      }
      if (result.trim_end_frame !== undefined) {
        setAppliedTrimEnd(result.trim_end_frame);
      }

    } catch (err) {
      if (cancelled?.current) return;
      console.error('Phase detection error:', err);
      setError(err instanceof Error ? err.message : 'Failed to detect phases');
    } finally {
      if (!cancelled?.current) setIsDetecting(false);
    }
  };

  // `runDetection` is read via a ref (not a dependency) so the auto-detect
  // effect below only fires on videoId change, per its comment/intent — not
  // on every render, and not when trimStartFrame/trimEndFrame/totalFrames
  // change (those require the manual "retry" button, `detectPhases` below).
  // The ref always holds the latest closure, so the call still uses
  // current trim/totalFrames values, same as before this was memo-safe.
  const runDetectionRef = useRef(runDetection);
  runDetectionRef.current = runDetection;

  // Auto-detect on mount/videoId change, with cleanup to prevent double-fire in strict mode
  useEffect(() => {
    const cancelled = { current: false };
    runDetectionRef.current(cancelled);
    return () => { cancelled.current = true; };
  }, [videoId]);

  // Manual retry handler (no cancellation needed)
  const detectPhases = () => runDetection();

  // Format a frame number as a human-readable time string (M:SS.s)
  const formatTime = (frame: number, fps: number): string => {
    const totalSecs = frame / fps;
    const minutes = Math.floor(totalSecs / 60);
    const secs = (totalSecs % 60).toFixed(1).padStart(4, '0');
    return `${minutes}:${secs}`;
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= CONFIDENCE.HIGH) return 'text-success bg-success/10';
    if (confidence >= CONFIDENCE.MEDIUM) return 'text-warning bg-warning/10';
    return 'text-danger bg-danger/10';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= CONFIDENCE.HIGH) return 'High';
    if (confidence >= CONFIDENCE.MEDIUM) return 'Medium';
    return 'Low';
  };

  const formatDuration = (startFrame: number, endFrame: number) => {
    const durationSeconds = (endFrame - startFrame + 1) / fps;
    return `${durationSeconds.toFixed(2)}s`;
  };

  const handleAccept = () => {
    onPhasesAccepted(detectedPhases);
  };

  if (isDetecting) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Detecting Movement Phases...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin h-10 w-10 text-accent" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-text-secondary">Analyzing video to detect movement phases...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Phase Detection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="error">{error}</Alert>
          <div className="flex gap-2">
            <Button onClick={detectPhases} variant="outline">Retry Detection</Button>
            <Button onClick={onSkip} variant="outline">Skip Phase Detection</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-accent/50">
      <CardHeader className="bg-accent/10">
        <CardTitle className="flex items-center gap-2">
          <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Detected Movement Phases
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasLowConfidence && (
          <Alert variant="warning">
            Low confidence phase detection. Review boundaries carefully and adjust if needed.
          </Alert>
        )}

        {/* Clip-range confirmation banner (B-2026-0102 Phase 1) */}
        {(appliedTrimStart !== undefined || trimStartFrame !== undefined) && (
          <div
            className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 flex items-center gap-2"
            data-testid="clip-detection-banner"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
            <span>
              Phases detected in your clip{' '}
              <span className="font-mono font-medium">
                ({formatTime(appliedTrimStart ?? trimStartFrame ?? 0, fps)}
                {' – '}
                {formatTime(
                  appliedTrimEnd ?? trimEndFrame ?? totalFrames - 1,
                  fps
                )})
              </span>
            </span>
          </div>
        )}

        <div className="space-y-3">
          {detectedPhases.map((phase, index) => (
            <div
              key={phase.id || index}
              className="border border-border-subtle rounded-lg p-4 bg-dark-elevated-1 hover:bg-dark-elevated-2 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-lg text-text-primary">{phase.name || phase.label || `Phase ${index + 1}`}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(phase.confidence || 0.5)}`}>
                      {getConfidenceLabel(phase.confidence || 0.5)} confidence ({((phase.confidence || 0.5) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  {phase.description && (
                    <p className="text-sm text-text-secondary">{phase.description}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-text-secondary">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Frames {phase.start_frame} - {phase.end_frame}</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>{formatDuration(phase.start_frame, phase.end_frame)}</span>
                </div>
              </div>

              {/* Visual timeline bar */}
              <div className="mt-3">
                <div className="h-2 bg-dark-elevated-2 rounded-full overflow-hidden relative">
                  <div
                    className="h-full bg-accent rounded-full absolute"
                    style={{
                      left: `${(phase.start_frame / totalFrames) * 100}%`,
                      width: `${((phase.end_frame - phase.start_frame + 1) / totalFrames) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {detectedPhases.length === 0 && (
          <Alert variant="info">
            No distinct phases detected. The movement appears to be continuous. You can still proceed with the template creation.
          </Alert>
        )}

        <div className="pt-4 border-t border-border-subtle">
          <p className="text-sm text-text-secondary mb-4">
            {detectedPhases.length > 0
              ? 'These phases have been automatically detected based on movement velocity analysis. You can accept them as-is, edit them, or start from scratch with manual phases.'
              : 'Phase detection did not find distinct segments. You can define phases manually.'}
          </p>
          <div className="flex gap-2">
            <Button onClick={onSkip} variant="outline">
              Start Manual
            </Button>
            {detectedPhases.length > 0 && (
              <>
                {onEditPhases && (
                  <Button
                    onClick={() => onEditPhases(detectedPhases)}
                    variant="outline"
                    className="border-accent/50 text-accent hover:bg-accent/10"
                  >
                    Edit Detected Phases
                  </Button>
                )}
                <Button onClick={handleAccept} className="flex-1 bg-accent hover:bg-accent-hover">
                  Accept As-Is
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
